'use client';
import React, { useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

const supplyData = [
  { name: 'Liquid Supply', value: 50, color: '#f7931a', desc: '50B FFCT — actively tradeable' },
  { name: 'Reserved (Coinbase)', value: 250, color: 'rgba(247,147,26,0.2)', desc: "250B FFCT — owner\'s Coinbase wallet" },
];

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; payload: { desc: string } }>;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload || !payload.length) return null;
  const item = payload[0];
  return (
    <div className="rounded-lg border border-zinc-700 bg-zinc-900 p-3 shadow-xl text-xs">
      <p className="text-white font-semibold mb-1">{item?.name}</p>
      <p className="font-mono text-btcgold-400">{item?.value}B FFCT ({item?.value ? ((item.value / 300) * 100).toFixed(1) : 0}%)</p>
      <p className="text-zinc-400 mt-1">{item?.payload?.desc}</p>
    </div>
  );
}

export default function SupplyChart() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  return (
    <div className="rounded-xl border border-zinc-800 bg-card p-5 flex flex-col gap-4">
      <div>
        <h3 className="text-sm font-semibold text-white">Supply Breakdown</h3>
        <p className="text-xs text-zinc-500 mt-0.5">300B FFCT total supply</p>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex-shrink-0">
          <ResponsiveContainer width={130} height={130}>
            <PieChart>
              <Pie
                data={supplyData}
                cx="50%"
                cy="50%"
                innerRadius={38}
                outerRadius={58}
                dataKey="value"
                strokeWidth={0}
                onMouseEnter={(_, index) => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                {supplyData.map((entry, index) => (
                  <Cell
                    key={`supply-cell-${index}`}
                    fill={entry.color}
                    opacity={activeIndex === null || activeIndex === index ? 1 : 0.5}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="flex flex-col gap-3 flex-1">
          {supplyData.map((item, index) => (
            <div key={`supply-legend-${index}`} className="flex flex-col gap-0.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: item.color === 'rgba(247,147,26,0.2)' ? '#f7931a' : item.color, opacity: item.color.includes('0.2') ? 0.5 : 1 }}
                  />
                  <span className="text-xs text-zinc-400">{item.name}</span>
                </div>
                <span className="text-xs font-mono font-semibold text-white">{item.value}B</span>
              </div>
              <div className="ml-4 w-full bg-zinc-800 rounded-full h-1">
                <div
                  className="h-1 rounded-full transition-all duration-300"
                  style={{
                    width: `${(item.value / 300) * 100}%`,
                    backgroundColor: index === 0 ? '#f7931a' : 'rgba(247,147,26,0.3)',
                  }}
                />
              </div>
            </div>
          ))}

          <div className="pt-1 border-t border-zinc-800">
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-500">Total Supply</span>
              <span className="text-xs font-mono font-bold text-white">300B FFCT</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}